import React, { useState } from 'react';
import { Link as LinkIcon, ExternalLink, GitBranch, Unlink, SlidersHorizontal } from 'lucide-react';
import { LinkedProjectConfig } from '../../types';
import { useIsMobile } from '../../hooks/useIsMobile';
import { BillingModeSwitchDialog } from './BillingModeSwitchDialog';
import { LinkedBillingMode, TAGESPAUSCHALE, billingModeLabelDe } from '../../services/linkedBillingMode';

// Banner oberhalb des Modul-Grids: zeigt entweder die Tochter-Rolle mit Absprung
// zum Mutter-Projekt oder den Einstieg in die Projekt-Verknüpfung.
//
// Auf dem Handy entfällt der Verknüpfungs-Einstieg komplett: Projekte werden am
// Schreibtisch verknüpft, unterwegs ist die Karte nur Platzverbrauch über dem
// Modul-Grid. Der Tochter-Banner bleibt — er ist Navigation zum Mutter-Projekt,
// keine Verknüpfungs-Funktion.

interface Props {
    linkedProjectConfig?: LinkedProjectConfig;
    linkedChildrenCount: number;
    isReadOnly: boolean;
    onOpenLinkedProject: () => void;
    /**
     * OEFFNET den Trennen-Dialog, trennt nicht selbst. Trennen ist der Umbau
     * zurueck: die beim Verknuepfen gewanderten Leistungen kommen an die
     * Tochter zurueck, und was bei der Mutter bleiben muss, wird vorher
     * benannt (ProjectUnlinkModal). Ein `window.confirm` an dieser Stelle hat
     * genau das jahrelang verschwiegen.
     */
    onUnlinkProject: () => void;
    onOpenLinkingModal: () => void;
    /**
     * Umstellung der Abrechnungsart. Fehlt sie, bleibt der Regler weg — der
     * Banner behaelt dann sein bisheriges Verhalten.
     */
    onChangeBillingMode?: (neu: LinkedBillingMode) => Promise<{ ok: boolean; message: string }>;
}

export const ProjectLinkBanner: React.FC<Props> = ({ linkedProjectConfig, linkedChildrenCount, isReadOnly, onOpenLinkedProject, onUnlinkProject, onOpenLinkingModal, onChangeBillingMode }) => {
    const isMobile = useIsMobile();
    const [zeigeUmstellung, setZeigeUmstellung] = useState(false);

    if (linkedProjectConfig?.role === 'child') {
        return (
            <div className="bg-blue-600 text-white rounded-xl p-4 shadow-lg flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg"><LinkIcon className="w-5 h-5" /></div>
                    <div>
                        <h3 className="font-bold text-sm uppercase tracking-wide">Tochter-Projekt</h3>
                        <p className="text-blue-100 text-xs">Verknüpft mit <strong>{linkedProjectConfig.linkedProjectName}</strong></p>
                        {/* Die Abrechnungsart gehoert sichtbar an den Banner: sie
                            entscheidet, ob eine Begehung am selben Tag wie die
                            Mutter eine eigene Leistung ist oder nicht — und war
                            bisher nirgends abzulesen. */}
                        <p className="text-blue-100 text-[11px] mt-0.5">
                            Abrechnung: <strong>{billingModeLabelDe(linkedProjectConfig.billingMode || TAGESPAUSCHALE)}</strong>
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {!isReadOnly && onChangeBillingMode && (
                        <button
                            onClick={() => setZeigeUmstellung(true)}
                            className="bg-blue-700 text-white px-3 py-2 rounded-lg text-xs font-bold hover:bg-blue-800"
                            title="Abrechnung gegenüber dem Mutterprojekt umstellen"
                        >
                            <SlidersHorizontal className="w-4 h-4" />
                        </button>
                    )}
                    <button onClick={onOpenLinkedProject} className="bg-white text-blue-600 px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 hover:bg-blue-50">Mutter-Projekt <ExternalLink className="w-3 h-3" /></button>
                    {!isReadOnly && <button onClick={onUnlinkProject} className="bg-blue-700 text-white px-3 py-2 rounded-lg text-xs font-bold hover:bg-blue-800" title="Trennen"><Unlink className="w-4 h-4" /></button>}
                </div>
                {zeigeUmstellung && onChangeBillingMode && (
                    <BillingModeSwitchDialog
                        aktuell={(linkedProjectConfig.billingMode || TAGESPAUSCHALE) as LinkedBillingMode}
                        mutterName={linkedProjectConfig.linkedProjectName}
                        onSwitch={onChangeBillingMode}
                        onClose={() => setZeigeUmstellung(false)}
                    />
                )}
            </div>
        );
    }
    if (isMobile) return null;
    return (
        <div className="bg-slate-100 rounded-xl p-4 border border-slate-200 flex justify-between items-center">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-lg shadow-sm"><GitBranch className="w-5 h-5 text-slate-500" /></div>
                <div>
                    <h3 className="font-bold text-sm text-slate-800">Projekt-Verknüpfung</h3>
                    <p className="text-slate-500 text-xs">{linkedChildrenCount > 0 ? `Mutterprojekt für ${linkedChildrenCount} Tochter-Projekt(e).` : 'Verknüpfen Sie dieses Projekt.'}</p>
                </div>
            </div>
            {!isReadOnly && <button onClick={onOpenLinkingModal} className="bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 hover:bg-slate-50"><LinkIcon className="w-4 h-4" /> Verknüpfen</button>}
        </div>
    );
};
